import {apiPath,mapActionToMethod ,ReqError } from '../../types/types';
import {buttonActionTypes} from '../../types/enums';
import { getValidUser } from '../../auth/AuthService';
import { userManager } from '../../auth/Auth';
import store from '../../configureStore';

const apiUrl = "__API_URL__";

// ✅ Common response parser
async function parseResponse(res: Response) {
  const text = await res.text();

  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return text;
  }
}

// ✅ Common auth fetch
async function fetchWithAuth(url: string, options: RequestInit = {}) {
  let user = await getValidUser();

  if (!user || !user.access_token) {
    throw new ReqError("Missing access token", "AuthError");
  }

  let token = user.access_token;
  let apimKey = store.getState().appSettings.apimSubKey;
  
  const makeRequest = async (accessToken: string) => {
      return fetch(url, {
        ...options,
        headers: {
          "Cache-Control": "no-cache",
          "Content-Type": "application/json",
          ...(options.headers || {}),
          Authorization: `Bearer ${accessToken}`,
          ...(apimKey && { "Ocp-Apim-Subscription-Key": apimKey })
        },
        // credentials: "include"
      });
    };

  let response = await makeRequest(token);

  // ✅ HANDLE 401 → retry once after refresh
  if (response.status === 401) {
    try {
      console.warn("Access token expired, attempting refresh...");

      user = await userManager.signinSilent(); // force refresh
      
      if (!user || !user.access_token) {
        throw new Error("No token after refresh");
      }

      token = user.access_token;

      response = await makeRequest(token); // ✅ retry with new token

      if (response.status === 401) {
        console.error("Retry after refresh still failed");
        throw new ReqError("Unauthorized after retry", "AuthError");
      }

    } catch (err) {
      console.error("Token refresh failed", err);
      throw new ReqError("Session expired. Please login again.", "AuthError");
    }
  }

  return response;

}

// ✅ POST
export async function callProArcApi(
  buttonAction: buttonActionTypes,
  postObject: any,
  param: string,
  param2?: string
): Promise<any> {

  const mapping: apiPath =
    param2 === undefined
      ? mapActionToMethod(buttonAction, param)
      : mapActionToMethod(buttonAction, param, param2);

  const completeURL = `${apiUrl}/${mapping.serviceName}/${mapping.methodName}`;

  let body = null;
  if (postObject && postObject.toString().trim() !== "") {
    body = JSON.stringify(postObject);
  }

  try {
    const res = await fetchWithAuth(completeURL, {
      method: "POST",
      body
    });

    return await parseResponse(res);
  } catch (error: any) {
    return new ReqError(JSON.stringify(error), "Error");
  }
}

// ✅ GET
export async function callProArcApiGet(
  buttonAction: buttonActionTypes,
  param: string,
  param2?: string
): Promise<any> {

  const mapping: apiPath =
    param2 === undefined
      ? mapActionToMethod(buttonAction, param)
      : mapActionToMethod(buttonAction, param, param2);

  const completeURL = `${apiUrl}/${mapping.serviceName}/${mapping.methodName}`;

  try {
    const res = await fetchWithAuth(completeURL, {
      method: "GET"
    });

    return await parseResponse(res);
  } catch (error: any) {
    return new ReqError(error, "Error");
  }
}

// ✅ DELETE
export async function callProArcApiDelete(
  buttonAction: buttonActionTypes,
  postObject: any,
  param: string,
  param2?: string
): Promise<any> {

  const mapping: apiPath =
    param2 === undefined
      ? mapActionToMethod(buttonAction, param)
      : mapActionToMethod(buttonAction, param, param2);

  const completeURL = `${apiUrl}/${mapping.serviceName}/${mapping.methodName}`;

  let body = null;
  if (postObject && postObject.toString().trim() !== "") {
    body = JSON.stringify(postObject);
  }

  try {
    const res = await fetchWithAuth(completeURL, {
      method: "DELETE",
      body
    });

    return await parseResponse(res);
  } catch (error: any) {
    return new ReqError(JSON.stringify(error), "Error");
  }
}